import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [settings, setSettings] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) {}
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("uemp_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadSettings();
    } catch (e) {
      localStorage.removeItem("uemp_token");
      setUser(false);
    }
  }, [loadSettings]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (username, password, company_id) => {
    const { data } = await api.post("/auth/login", { username, password, company_id });
    if (data.access_token) localStorage.setItem("uemp_token", data.access_token);
    setUser(data.user);
    loadSettings();
    return data.user;
  }, [loadSettings]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    localStorage.removeItem("uemp_token");
    setUser(false);
  }, []);

  const hasPerm = (code) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes("*") || user.permissions.includes(code);
  };

  // ---- Session inactivity timeout (backend-enforced; this is the warning UX) ----
  const lastActivityRef = useRef(Date.now());
  const lastBeatRef = useRef(Date.now());
  const warnedRef = useRef(false);

  useEffect(() => {
    const idleMin = settings?.session_idle_minutes;
    if (!user || !idleMin) return;
    const idleMs = idleMin * 60 * 1000;
    const warnMs = Math.max(idleMs - 60 * 1000, 10 * 1000);

    const bump = () => {
      lastActivityRef.current = Date.now();
      warnedRef.current = false;
      // Keep the server-side session alive while the user is actually interacting.
      if (Date.now() - lastBeatRef.current > 60000) {
        lastBeatRef.current = Date.now();
        api.get("/auth/me").catch(() => {});
      }
    };
    const events = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const iv = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= idleMs) {
        toast.error("Signed out due to inactivity.");
        logout();
      } else if (idle >= warnMs && !warnedRef.current) {
        warnedRef.current = true;
        toast.warning("You'll be signed out soon due to inactivity.", {
          duration: 55000,
          action: {
            label: "Stay signed in",
            onClick: () => {
              lastActivityRef.current = Date.now();
              lastBeatRef.current = Date.now();
              warnedRef.current = false;
              api.get("/auth/me").catch(() => {});
            },
          },
        });
      }
    }, 10000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(iv);
    };
  }, [user, settings, logout]);

  return (
    <AuthContext.Provider value={{ user, setUser, settings, loadSettings, login, logout, hasPerm }}>
      {children}
    </AuthContext.Provider>
  );
}
